import { z } from "zod";
import { AccountId } from "./ids.js";
import { MessageSource } from "./sources.js";
/**
 * Message kinds and raw capture (architecture §6.4, §6.7; WP-061). Every message and thread has a
 * kind; the inbox lists people and folds the other kinds into one row each.
 */
export const MessageKind = z.enum(["person", "notification", "newsletter", "receipt"]);
/** The kinds a listing can be narrowed to: one kind, or `all` for the unified list. */
export const KindFilter = z.enum(["person", "notification", "newsletter", "receipt", "all"]);
/**
 * Why `classify` chose the kind, shown as "Why here?". One code per signal (§6.7 precedence):
 * an override (063: the owner's for the address, `override`, or for its domain, `override-domain`;
 * Claude's suggestion, `suggested`), a known app (062), a list header, an automation header or
 * address, a provider hint, a known correspondent, or nothing (`default`).
 */
export const KindReason = z.enum([
    "override",
    "override-domain",
    "suggested",
    "app",
    "list-id",
    "list-unsubscribe",
    "feedback-id",
    "auto-submitted",
    "precedence",
    "automated-address",
    "gmail-promotions",
    "gmail-updates",
    "gmail-social",
    "gmail-forums",
    "outlook-other",
    "correspondent",
    "default",
]);
/** What `classify` decides for one message. */
export const Classification = z.object({
    kind: MessageKind,
    kindReason: KindReason,
    source: MessageSource.nullable(),
});
/** One header as the provider returned it; order matters (`Received` is newest first). */
export const RawHeader = z.object({ name: z.string(), value: z.string() });
/**
 * Everything a provider returned about one message except its body and attachment bytes: the
 * headers in order, and every other field as the provider sent it (Gmail `labelIds`,
 * `historyId`, `internalDate`, `sizeEstimate`; Graph every property but `body`/`uniqueBody`).
 * Sync batches carry one per upserted message, keyed by provider id.
 */
export const MessageRaw = z.object({
    id: z.string().min(1),
    headers: z.array(RawHeader),
    provider: z.record(z.string(), z.unknown()),
});
/** Largest `messageRaw` document sync writes; Firestore's limit is 1 MiB. */
export const MESSAGE_RAW_MAX_BYTES = 900 * 1024;
/**
 * `accounts/{a}/messageRaw/{msgId}`: server-only (rules deny clients). The api serves it to the
 * owner as "Show original" (`mail.getRaw`). `truncated` is set when the document had to be cut
 * to fit `MESSAGE_RAW_MAX_BYTES`.
 */
export const MessageRawDoc = z.object({
    ownerUid: z.string().min(1),
    accountId: AccountId,
    headers: z.array(RawHeader),
    provider: z.record(z.string(), z.unknown()),
    capturedAt: z.date(),
    truncated: z.boolean(),
});
/**
 * `accounts/{a}/correspondents/{sha256(lowercased address)}`: an address the owner has sent to
 * from this account (sync only). A sender here is a person unless list headers say otherwise.
 */
export const CorrespondentDoc = z.object({
    lastSentAt: z.date(),
});
//# sourceMappingURL=kinds.js.map